import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaShieldAlt, FaKey, FaLock, FaUnlock } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dev.brdz.xyz';
const API = `${BACKEND_URL}/api`;

export default function CustodyChoice() {
  const [loading, setLoading] = useState(false);
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [mnemonic, setMnemonic] = useState('');
  const navigate = useNavigate();

  const chooseCustody = async (mode) => {
    setLoading(true);
    const token = localStorage.getItem('token');

    if (!token) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    try {
      const res = await axios.post(
        `${API}/choose-custody`,
        { custody: mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.mode === 'noncustodial' && res.data.mnemonic) {
        setMnemonic(res.data.mnemonic);
        setShowMnemonic(true);
      } else {
        toast.success('Wallet setup complete!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleMnemonicAcknowledge = () => {
    toast.success('Wallet setup complete!');
    navigate('/dashboard');
  };

  if (showMnemonic) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="relative z-10 w-full max-w-2xl fade-in-up">
          <div className="bg-[#0f172a] border border-[#ccff00] p-8">
            <div className="text-center mb-6">
              <FaKey className="text-[#ccff00] text-5xl mx-auto mb-4" />
              <h2 className="text-3xl font-extrabold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                Your Seed Phrase
              </h2>
              <p className="text-[#94a3b8] text-sm">
                Save this seed phrase in a secure location. You'll need it to recover your wallet.
              </p>
            </div>

            <div className="bg-[#020617] border border-[#1e293b] p-6 mb-6">
              <div className="grid grid-cols-3 gap-3 mono text-[#ccff00] text-sm">
                {mnemonic.split(' ').map((word, idx) => (
                  <div key={idx} className="bg-[#0f172a] p-3 border border-[#1e293b]">
                    <span className="text-[#94a3b8] mr-2">{idx + 1}.</span>
                    {word}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#7f1d1d] border border-[#ef4444] p-4 mb-6">
              <p className="text-[#fca5a5] text-sm font-medium">
                <strong>WARNING:</strong> Never share your seed phrase with anyone. Store it offline in a secure location.
              </p>
            </div>

            <button
              onClick={handleMnemonicAcknowledge}
              className="w-full bg-[#ccff00] text-black font-bold uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#b3e600] transition-all"
              style={{ fontFamily: 'Manrope' }}
              data-testid="acknowledge-mnemonic-button"
            >
              I've Saved My Seed Phrase
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-5xl fade-in-up">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-extrabold tracking-tight text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Choose Your Custody Mode
          </h1>
          <p className="text-[#94a3b8] text-lg">
            Select how you want to manage your wallet
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Custodial Card */}
          <div
            className="tracing-beam-card bg-[#0f172a] border border-[#1e293b] p-8 group cursor-pointer hover:border-[#ccff00]/50"
            onClick={() => !loading && chooseCustody('custodial')}
            data-testid="custodial-mode-card"
          >
            <div
              className="w-full h-48 mb-6 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1519995451813-39e29e054914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxldGhlcmV1bSUyMGJpdGNvaW4lMjBzb2xhbmElMjB0ZXRoZXIlMjBjcnlwdG8lMjBsb2dvcyUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NjUwNzk1MTJ8MA&ixlib=rb-4.1.0&q=85')`,
              }}
            />
            <div className="flex items-center justify-center mb-4">
              <FaShieldAlt className="text-[#ccff00] text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Manrope' }}>
              Custodial
            </h3>
            <p className="text-[#94a3b8] text-sm mb-6 text-center">
              We securely store your private keys. Easier UX, managed security.
            </p>

            <div className="space-y-2 mb-6">
              <div className="flex items-start">
                <div className="text-[#22c55e] mr-2">✓</div>
                <span className="text-[#94a3b8] text-sm">Easy recovery process</span>
              </div>
              <div className="flex items-start">
                <div className="text-[#22c55e] mr-2">✓</div>
                <span className="text-[#94a3b8] text-sm">No seed phrase management</span>
              </div>
              <div className="flex items-start">
                <div className="text-[#ef4444] mr-2">✗</div>
                <span className="text-[#94a3b8] text-sm">We hold your keys</span>
              </div>
            </div>

            <button
              className="w-full bg-[#ccff00] text-black font-bold uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#b3e600] transition-all"
              style={{ fontFamily: 'Manrope' }}
              disabled={loading}
            >
              {loading ? 'Setting up...' : 'Select Custodial'}
            </button>
          </div>

          {/* Non-Custodial Card */}
          <div
            className="tracing-beam-card bg-[#0f172a] border border-[#1e293b] p-8 group cursor-pointer hover:border-[#ccff00]/50"
            onClick={() => !loading && chooseCustody('noncustodial')}
            data-testid="non-custodial-mode-card"
          >
            <div
              className="w-full h-48 mb-6 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1669060534046-6271062c85b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxldGhlcmV1bSUyMGJpdGNvaW4lMjBzb2xhbmElMjB0ZXRoZXIlMjBjcnlwdG8lMjBsb2dvcyUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NjUwNzk1MTJ8MA&ixlib=rb-4.1.0&q=85')`,
              }}
            />
            <div className="flex items-center justify-center mb-4">
              <FaKey className="text-[#ccff00] text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3 text-center" style={{ fontFamily: 'Manrope' }}>
              Non-Custodial
            </h3>
            <p className="text-[#94a3b8] text-sm mb-6 text-center">
              You control your private keys. Full ownership, full responsibility.
            </p>

            <div className="space-y-2 mb-6">
              <div className="flex items-start">
                <div className="text-[#22c55e] mr-2">✓</div>
                <span className="text-[#94a3b8] text-sm">You own your keys</span>
              </div>
              <div className="flex items-start">
                <div className="text-[#22c55e] mr-2">✓</div>
                <span className="text-[#94a3b8] text-sm">Maximum security</span>
              </div>
              <div className="flex items-start">
                <div className="text-[#ef4444] mr-2">✗</div>
                <span className="text-[#94a3b8] text-sm">Must save seed phrase</span>
              </div>
            </div>

            <button
              className="w-full bg-transparent border border-[#ccff00] text-[#ccff00] font-bold uppercase tracking-wider text-sm px-8 py-3 hover:bg-[#ccff00] hover:text-black transition-all"
              style={{ fontFamily: 'Manrope' }}
              disabled={loading}
            >
              {loading ? 'Setting up...' : 'Select Non-Custodial'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}